/**
 * Admin — Kullanıcı Yönetimi
 *
 *   /admin/kullanicilar
 *
 * Liste + arama + filter + "+ Yeni" + staff toggle + şifre sıfırlama + deactivate.
 */
import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface UserRow {
  id: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  isActive: boolean;
  isBmuStaff: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  scheduledDeleteAt: string | null;
  _count: { organizationMemberships: number };
}

interface CreateUserResult {
  user: { id: string; email: string; name: string; isBmuStaff: boolean };
  initialPassword: string;
  note: string;
}

interface ResetPwResult {
  userId: string;
  email: string;
  newPassword: string;
  note: string;
}

export function AdminUsersPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending } = useSession();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const initialQ = params.get('q') ?? '';

  const [users, setUsers] = useState<UserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState(initialQ);
  const [filter, setFilter] = useState<'' | 'staff' | 'inactive' | 'unverified'>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [resetPwResult, setResetPwResult] = useState<ResetPwResult | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (filter) p.set('filter', filter);
      p.set('limit', '100');
      const data = await api.get<{ users: UserRow[]; total: number }>(
        `/api/admin/users?${p.toString()}`,
      );
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Liste yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isPending, q, filter]);

  async function toggleStaff(id: string, current: boolean): Promise<void> {
    try {
      await api.patch(`/api/admin/users/${id}`, { isBmuStaff: !current });
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function toggleActive(id: string, current: boolean): Promise<void> {
    try {
      await api.patch(`/api/admin/users/${id}`, { isActive: !current });
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function resetPassword(id: string): Promise<void> {
    if (
      !confirm(
        'Yeni rastgele şifre üretip kullanıcıya iletmeniz için göstereyim mi?',
      )
    )
      return;
    try {
      const data = await api.post<ResetPwResult>(
        `/api/admin/users/${id}/reset-password`,
        {},
      );
      setResetPwResult(data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  async function deactivate(id: string, email: string): Promise<void> {
    if (!confirm(`${email} pasifleştirilsin mi? 30 gün sonra otomatik anonimize.`))
      return;
    try {
      await api.delete(`/api/admin/users/${id}`);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => setLocation('/admin/dashboard')}
              className="text-sm font-medium text-blue-900 hover:underline"
            >
              ← Admin Panel
            </button>
            <h1 className="mt-2 text-3xl font-bold text-slate-900">
              Kullanıcı Yönetimi
            </h1>
            <p className="text-sm text-slate-600">
              Tüm kullanıcılar. Yeni kullanıcı yarat, staff yetkisi ver, şifre
              sıfırla.
            </p>
          </div>
          <Button onClick={() => setShowCreate(true)}>+ Yeni Kullanıcı</Button>
        </div>

        {error && (
          <div className="mb-4">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {showCreate && (
          <CreateUserModal
            onClose={() => setShowCreate(false)}
            onCreated={async () => {
              setShowCreate(false);
              await refresh();
            }}
          />
        )}

        {resetPwResult && (
          <ResetPasswordModal
            result={resetPwResult}
            onClose={() => setResetPwResult(null)}
          />
        )}

        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              type="text"
              placeholder="E-posta veya ad ara…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-sm"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">
                Filtre:
              </span>
              {(['', 'staff', 'inactive', 'unverified'] as const).map((f) => (
                <button
                  key={f || 'all'}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    filter === f
                      ? 'bg-blue-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {f === '' ? 'Tümü' : f}
                </button>
              ))}
            </div>
            <span className="ml-auto text-sm text-slate-500">
              {total} kullanıcı
            </span>
          </div>
        </Card>

        {loading ? (
          <p className="text-slate-500">Yükleniyor…</p>
        ) : users.length === 0 ? (
          <Card>
            <p className="text-center text-slate-500">
              Kriterle eşleşen kullanıcı yok.
            </p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="py-2 pr-3">Kullanıcı</th>
                    <th className="py-2 pr-3">Durum</th>
                    <th className="py-2 pr-3 text-right">Firma</th>
                    <th className="py-2 pr-3">Son Giriş</th>
                    <th className="py-2 pl-3 text-right">Aksiyon</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className={!u.isActive ? 'opacity-60' : ''}
                    >
                      <td className="py-2 pr-3">
                        <p className="font-medium text-slate-900">
                          {u.name ?? '—'}
                        </p>
                        <p className="text-xs text-slate-500">{u.email}</p>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {u.isBmuStaff && (
                            <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-900">
                              STAFF
                            </span>
                          )}
                          {!u.emailVerified && (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                              doğrulanmamış
                            </span>
                          )}
                          {!u.isActive && (
                            <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">
                              pasif
                            </span>
                          )}
                          {u.scheduledDeleteAt && (
                            <span
                              className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-900"
                              title={`Silme: ${new Date(u.scheduledDeleteAt).toLocaleDateString('tr-TR')}`}
                            >
                              silme bekliyor
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono">
                        {u._count.organizationMemberships}
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-500">
                        {u.lastSeenAt
                          ? new Date(u.lastSeenAt).toLocaleDateString('tr-TR')
                          : '—'}
                      </td>
                      <td className="py-2 pl-3 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => toggleStaff(u.id, u.isBmuStaff)}
                            className={`rounded-md border px-2 py-1 text-xs ${
                              u.isBmuStaff
                                ? 'border-purple-300 text-purple-700 hover:bg-purple-50'
                                : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                            }`}
                            title={u.isBmuStaff ? 'Staff bayrağını kaldır' : 'Staff yap'}
                          >
                            {u.isBmuStaff ? '👑 Kaldır' : '👑 Staff'}
                          </button>
                          <button
                            type="button"
                            onClick={() => resetPassword(u.id)}
                            className="rounded-md border border-blue-300 px-2 py-1 text-xs text-blue-900 hover:bg-blue-50"
                          >
                            🔑 Şifre
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleActive(u.id, u.isActive)}
                            className={`rounded-md border px-2 py-1 text-xs ${
                              u.isActive
                                ? 'border-amber-300 text-amber-800 hover:bg-amber-50'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                            }`}
                          >
                            {u.isActive ? 'Pasifleştir' : 'Aktifleştir'}
                          </button>
                          {u.isActive && (
                            <button
                              type="button"
                              onClick={() => deactivate(u.id, u.email)}
                              className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}

// ─── Create User Modal ──────────────────────────────────────────

function CreateUserModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isBmuStaff, setIsBmuStaff] = useState(false);
  const [customPassword, setCustomPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateUserResult | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { email, name, isBmuStaff };
      if (customPassword && password) body['password'] = password;
      const data = await api.post<CreateUserResult>('/api/admin/users', body);
      setResult(data);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Yaratılamadı');
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
        <Card className="w-full max-w-md">
          <Alert variant="success">
            <strong>Kullanıcı yaratıldı.</strong> {result.note}
          </Alert>
          <div className="mt-4 space-y-1 text-sm">
            <p>
              <strong>{result.user.name}</strong> · {result.user.email}
              {result.user.isBmuStaff && (
                <span className="ml-2 rounded-full bg-purple-50 px-2 py-0.5 text-xs text-purple-900">
                  STAFF
                </span>
              )}
            </p>
          </div>
          <div className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-900">
              Şifre (tek seferlik)
            </p>
            <p className="mt-1 font-mono text-base text-slate-900">
              {result.initialPassword}
            </p>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(result.initialPassword);
              }}
              className="mt-2 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
            >
              Kopyala
            </button>
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              onClick={async () => {
                await onCreated();
              }}
            >
              Tamamla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <h3 className="text-lg font-semibold text-slate-900">Yeni Kullanıcı</h3>
        <p className="mt-1 text-sm text-slate-600">
          Email doğrulama otomatik atlatılır (BMU staff tarafından yaratıldı).
        </p>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        <form onSubmit={submit} className="mt-4" noValidate>
          <Field label="E-posta" htmlFor="u-email" required>
            <Input
              id="u-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
              required
            />
          </Field>
          <Field label="İsim" htmlFor="u-name" required>
            <Input
              id="u-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Field>
          <Checkbox
            id="u-staff"
            label="BMU Iç Ekip (Staff) — admin paneline erişebilir"
            checked={isBmuStaff}
            onChange={(e) => setIsBmuStaff(e.target.checked)}
          />
          <Checkbox
            id="u-cpw"
            label="Şifreyi manuel gireyim (boş bırak → auto-generate)"
            checked={customPassword}
            onChange={(e) => setCustomPassword(e.target.checked)}
          />
          {customPassword && (
            <Field
              label="Şifre"
              htmlFor="u-pw"
              hint="En az 8 karakter, 1 büyük + 1 küçük + 1 rakam"
            >
              <Input
                id="u-pw"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={submitting}
            >
              İptal
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={!email || !name}
            >
              Oluştur
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ResetPasswordModal({
  result,
  onClose,
}: {
  result: ResetPwResult;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <Alert variant="success">
          <strong>Şifre sıfırlandı.</strong> {result.note}
        </Alert>
        <p className="mt-3 text-sm text-slate-700">
          <strong>{result.email}</strong> için yeni şifre:
        </p>
        <div className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-3">
          <p className="font-mono text-base text-slate-900">
            {result.newPassword}
          </p>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(result.newPassword);
            }}
            className="mt-2 rounded-md border border-amber-400 px-3 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Kopyala
          </button>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={onClose}>Tamamla</Button>
        </div>
      </Card>
    </div>
  );
}
